import {
  Component,
  EventEmitter,
  OnInit,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { DxListComponent } from 'devextreme-angular';
import { OrganisationUnitModel } from 'src/app/models/organisation-unit/organisation-unit.model';
import { PersonModel } from 'src/app/models/person/person.model';
import { TrainingInvitationSearchModel } from 'src/app/models/training/training-invitation-search.model';
import { LoadingService } from 'src/app/services/loading.service';
import { OrganisationUnitService } from 'src/app/services/organisation-unit.service';
import { TrainingMessageService } from 'src/app/services/training-message.service';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { PersonService } from 'src/app/services/person.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { EventMemberService } from 'src/app/services/event-member.service';
import { TrainingGroupModel } from 'src/app/models/training-group.model';
import { forkJoin, shareReplay } from 'rxjs';
import { TrainingGroupService } from 'src/app/services/training-group.service';
import { MergeUnknownEventMemberModel } from 'src/app/models/events/merge-unknown-event-member.model';
import { ProfileModel } from 'src/app/models/profile.model';

@Component({
  selector: 'app-merge-person-modal',
  standalone: false,
  templateUrl: './merge-person-modal.component.html',
  styleUrl: './merge-person-modal.component.scss'
})
export class MergePersonModalComponent implements OnInit {
  @ViewChild('list', { static: true })
  list!: DxListComponent;

  @Input() showModal = false;
  @Output() showModalChange = new EventEmitter<boolean>();

  @Input() 
  trainingId!: string;

  @Input() 
  excludePersonIds: string[] = [];

  @Input()
  mergePerson!: ProfileModel;

  @Output() onMerge = new EventEmitter<PersonModel>();
  @Output() onCancel = new EventEmitter();

  organisationUnits: OrganisationUnitModel[] = [];
  trainingGroups: TrainingGroupModel[] = [];
  
  allPersons: PersonModel[] = [];
  selectedPerson: PersonModel|null = null;
  page: number = 0;

  isSearchLimit: boolean = false;

  formData = {
    organisationUnitFilter: [] as string[],
    trainingGroupFilter: [] as string[],
    search: '',
  };

  store!: CustomStore;
  dataSource!: DataSource;

  constructor(
    private organisationUnitService: OrganisationUnitService,
    private trainingInvitationService: TrainingMessageService,
    private personService: PersonService,
    private loadingService: LoadingService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private eventMemberService: EventMemberService,
    private trainingGroupService: TrainingGroupService
  ) {}

  ngOnInit(): void {
    const formData = this.loadingService
      .load(
        forkJoin({
          trainingGroups: this.trainingGroupService.getAllTrainingGroups(),
          organisationUnits:
            this.organisationUnitService.getAllOrganisationUnits(),
        })
      )
      .pipe(shareReplay(1));

    formData.subscribe(
      ({
        trainingGroups,
        organisationUnits,
        
      }) => {
        this.trainingGroups = trainingGroups;
        this.organisationUnits = organisationUnits;
        this.doSearch(true);
      });
  }

  onOrganizationUnitsSelected(e: any) {
    const currentSelectionId = e.itemData.id;
    const isSelection = e.node.selected;

    const currentUnit = this.organisationUnits.find(
      (unit) => unit.id === currentSelectionId
    );

    if (currentUnit) {
      currentUnit.selected = isSelection;
    }

    const setChildrenSelection = (parentId: string, selected: boolean) => {
      this.organisationUnits.forEach((unit) => {
        if (unit.parentId === parentId) {
          unit.selected = selected;
          setChildrenSelection(unit.id, selected);
        }
      });
    };

    setChildrenSelection(currentSelectionId, isSelection);

    const checkAndDeselectParents = (childParentId: string | null) => {
      if (!childParentId) return;

      const siblings = this.organisationUnits.filter(
        (unit) => unit.parentId === childParentId
      );
      const allSelected = siblings.every((s) => s.selected);

      if (!allSelected) {
        const parentUnit = this.organisationUnits.find(
          (unit) => unit.id === childParentId
        );
        if (parentUnit && parentUnit.selected) {
          parentUnit.selected = false;
          checkAndDeselectParents(parentUnit.parentId);
        }
      }
    };

    if (!isSelection) {
      checkAndDeselectParents(currentUnit?.parentId ?? null);
    }

    const selectedOrganisationUnitsIds = this.organisationUnits
      .filter((unit) => unit.selected)
      .map((unit) => unit.id);
    this.formData.organisationUnitFilter = selectedOrganisationUnitsIds;
    
    this.organisationUnits = [...this.organisationUnits];
    this.doSearch(true);
  }

  onTrainingGroupSelected(e: any) {
    if (e.node.selected) {
      this.formData.trainingGroupFilter = [
        ...this.formData.trainingGroupFilter,
        e.node.key,
      ];
    } else {
      this.formData.trainingGroupFilter =
        this.formData.trainingGroupFilter.filter((id) => id !== e.node.key);
    }

    this.doSearch(true);
  }


  onSearchValueChanged(e: any) {
    this.doSearch(true);
  }

  doSearch(resetSearch: boolean): void {
    if (!resetSearch && this.isSearchLimit) {
      return;
    } else if (resetSearch) {
      this.isSearchLimit = false;
    }

    const page = resetSearch ? 0 : this.page + 1;
    const model: TrainingInvitationSearchModel = {
      page,
      searchText: this.formData.search,
      organisationUnitIdFilter: this.formData.organisationUnitFilter,
      trainingGroupIdFilter: this.formData.trainingGroupFilter,
      excludePersonIds: this.excludePersonIds,
      allPersonsSelected: false
    };

    this.loadingService
      .load(this.trainingInvitationService.searchPersonsForInvitation(model))
      .subscribe((result) => {
        if (!result.persons.length) {
          this.isSearchLimit = true;
        }

        this.page = result.page;
        const data = result.persons.map((person: PersonModel) => {
          person.selected = false;
          return person;
        });

        this.allPersons = resetSearch
          ? data
          : this.allPersons.concat(data);
      });
  }

  onScrolled(event: any) {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;

    if (scrollTop + offsetHeight >= scrollHeight) {
      this.doSearch(false);
    }
  }

  onPersonSelected(e: any, person: PersonModel) {
    if(this.selectedPerson && this.selectedPerson.id === person.id) {
      this.selectedPerson = null;
    } else {
      this.selectedPerson = person;
    }
  }

  onScroll(event: any) {
    const element = event.target;
    // Check if the user has scrolled to the bottom
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 1) {
      this.doSearch(false)
    }
  }

  onMergeClick = () => {
    if (!this.selectedPerson) return;

    if (this.mergePerson.id && this.mergePerson.isUnknownUser !== true) {
      this.loadingService.load(
        this.personService.mergePerson(this.mergePerson.id, this.selectedPerson.id)
      ).subscribe({
        next: () => {
          this.toastrService.success(
            this.translateService.instant('EMPLOYEE.OVERVIEW.MERGE-SUCCESS')
          );
          this.onMerge.emit(this.selectedPerson!);
          this.toggleShowModal();
        },
        error: () => {
          this.toastrService.error(
            this.translateService.instant('EMPLOYEE.OVERVIEW.MERGE-ERROR')
          );
        }
      });
    } else if(this.mergePerson.eventMemberUnknownId) {
      const model: MergeUnknownEventMemberModel = {
        personId: this.selectedPerson.id,
        eventId: this.trainingId,
        eventMemberUnknownId: this.mergePerson.eventMemberUnknownId,
      };

      this.loadingService.load(
        this.eventMemberService.mergeUnknownEventMember(model)
      ).subscribe({
        next: () => {
          this.toastrService.success(
            this.translateService.instant('EMPLOYEE.OVERVIEW.MERGE-SUCCESS')
          );
          this.onMerge.emit(this.selectedPerson!);
          this.toggleShowModal();
        },
        error: () => {
          this.toastrService.error(
            this.translateService.instant('EVENT-MEMBER.ADD-ATTENDANCE-ERROR')
          );
        }
      });
    }
  };

 
  onCancelClick = () => {
    this.onCancel.emit();
    this.toggleShowModal();
  };

  toggleShowModal() {
    this.showModal = !this.showModal;
    this.showModalChange.emit(this.showModal);
  }
}
